import React, { FC, useEffect } from "react";

import { useDataLayer } from "../../hooks/use-data-layer";
import { GoogleAnalyticsEvent } from "../../utils/analytics";
import {
  CustomerStorySection,
  CustomerStorySectionProps,
} from "./customer-story-strip/customer-story-section";
import { ExhibitionSection, ExhibitionSectionProps } from "./exhibition-strip/exhibition-section";
import { ProductSection, ProductSectionProps } from "./product-strip/product-section";
import { WhatsNextStrip, WhatsNextStripProps } from "./whats-next-strip/whats-next-strip";

type EnrichmentRendererProps = {} & (
  | ({ __typename: "ContentfulCustomerStory" } & CustomerStorySectionProps)
  | ({ __typename: "ContentfulExhibition" } & ExhibitionSectionProps)
  | ({ __typename: "ContentfulProduct" } & ProductSectionProps)
  | ({ __typename: "ContentfulWhatsNext" } & WhatsNextStripProps)
);

export const EnrichmentRenderer: FC<EnrichmentRendererProps> = ({ ...props }) => {
  const { __typename } = props;

  const { pushEvent } = useDataLayer();
  useEffect(() => {
    switch (props.__typename) {
      case "ContentfulCustomerStory":
        if (props.defaultLocaleEntry) {
          pushEvent({
            event: GoogleAnalyticsEvent.CustomerStoryView,
            customer_story_name: props.defaultLocaleEntry.name,
            material: props.defaultLocaleEntry.specialization?.machiningType?.material ?? null,
            label1: props.defaultLocaleEntry.specialization?.name,
          });
        }
        break;
      case "ContentfulExhibition":
        if (props.defaultLocaleEntry) {
          pushEvent({
            event: GoogleAnalyticsEvent.ViewExhibitionInfo,
            exhibition_name: props.defaultLocaleEntry.title,
          });
        }
        break;
      case "ContentfulProduct":
        if (props.defaultLocaleEntry) {
          pushEvent(
            {
              event: GoogleAnalyticsEvent.ProductView,
              product_name: props.defaultLocaleEntry.name,
              product_id: props.defaultLocaleEntry.id,
              product_category: props.defaultLocaleEntry.specialization?.name ?? null,
              product_category2:
                props.defaultLocaleEntry.specialization?.machiningType?.name ?? null,
              product_category3:
                props.defaultLocaleEntry.specialization?.machiningType?.material ?? null,
            },
            ["product_id"]
          );
        }
        break;
      case "ContentfulWhatsNext":
        if (props.defaultLocaleEntry) {
          pushEvent({
            event: GoogleAnalyticsEvent.NewsView,
            news_name: props.defaultLocaleEntry.title,
            label1: props.defaultLocaleEntry.categories?.[0]?.label ?? null,
            label2: props.defaultLocaleEntry.categories?.[1]?.label ?? null,
          });
        }
        break;
    }
  }, [pushEvent, props]);

  switch (__typename) {
    case "ContentfulCustomerStory":
      return <CustomerStorySection {...props} />;
    case "ContentfulExhibition":
      return <ExhibitionSection {...props} />;
    case "ContentfulProduct":
      return <ProductSection {...props} />;
    case "ContentfulWhatsNext":
      return <WhatsNextStrip {...props} />;
    default:
      console.error("Unable to render", __typename);
      return null;
  }
};
